'use client';

import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Sparkles, Heart, Flame, Lock, Users, Star,
  Circle, Zap, Droplet, Wind, Moon, Sun,
  ArrowLeft, Check, Play, X
} from 'lucide-react';
import type { LandingPageData } from '@/lib/landing/mapping';

interface QuizProps {
  onVoltar: () => void;
  inModal?: boolean;
  landingData: LandingPageData;
}

const HEADING = { fontFamily: 'var(--font-heading)' };
const BODY = { fontFamily: 'var(--font-body)' };

type QuizOption = {
  label: string;
  scores: Record<string, number>;
};

type QuizQuestion = {
  id: string;
  texto: string;
  subtexto: string | null;
  opcoes: QuizOption[];
};

type QuizResultado = {
  principal: TrilhaInfo;
  secundaria: TrilhaInfo | null;
  terceira: TrilhaInfo | null;
};
type TrilhaInfo = {
  id: string;
  nome: string;
  icon: typeof Sparkles;
  cor: string;
  gradiente: string;
  descricao: string;
  quizResultText?: string;
  tags: string[];
  imagem: string;
  imagemAlt: string;
};

function calcularResultado(respostas: QuizOption[], trilhasById: Record<string, TrilhaInfo>): QuizResultado | null {
  const totais: Record<string, number> = {};
  respostas.forEach((opcao) => {
    Object.entries(opcao.scores).forEach(([trilha, pts]) => {
      totais[trilha] = (totais[trilha] || 0) + Number(pts);
    });
  });
  const sorted = Object.entries(totais).sort((a, b) => b[1] - a[1]);
  const picks = sorted.map(([trilhaId]) => trilhasById[trilhaId]).filter(Boolean);
  const principal = picks[0];
  if (!principal) return null;
  return { principal, secundaria: picks[1] ?? null, terceira: picks[2] ?? null };
}

export function Quiz({ onVoltar, inModal = false, landingData }: QuizProps) {
  const [fase, setFase] = useState<'intro' | 'quiz' | 'resultado'>('intro');
  const [atual, setAtual] = useState(0);
  const [respostas, setRespostas] = useState<QuizOption[]>([]);
  const [selecionadaIndex, setSelecionadaIndex] = useState<number | null>(null);
  const [resultado, setResultado] = useState<QuizResultado | null>(null);
  const [revelarSecundarias, setRevelarSecundarias] = useState(false);

  const quizTitle = landingData.quiz.title ?? 'Qual trilha combina com você?';
  const quizSubtitle = landingData.quiz.subtitle ?? '';
  const quizCtaTrilhaLink = landingData.quiz.ctaTrilhaLink ?? '';

  const trilhasById = Object.fromEntries(
    landingData.trilhas
      .filter((t): t is Required<Pick<TrilhaInfo, 'id'>> & Partial<TrilhaInfo> => Boolean(t.id))
      .map((t) => [
        t.id as string,
        {
          id: t.id as string,
          nome: t.nome ?? '',
          icon: (t.icon ?? Sparkles) as typeof Sparkles,
          cor: t.cor ?? '#FF006E',
          gradiente: t.gradiente ?? 'from-pink-500 to-purple-600',
          descricao: t.descricao ?? '',
          quizResultText: t.quizResultText,
          tags: [],
          imagem: t.imagem ?? '',
          imagemAlt: t.imagemAlt ?? t.nome ?? '',
        },
      ]),
  ) as Record<string, TrilhaInfo>;

  const perguntas = (landingData.quiz.questions ?? [])
    .map((q) => ({
      id: q.id ?? '',
      texto: q.texto ?? '',
      subtexto: q.subtexto ?? null,
      opcoes: (q.opcoes ?? [])
        .map((o) => ({ label: o.label ?? '', scores: o.scores ?? {} }))
        .filter((o) => o.label.length > 0 && Object.keys(o.scores).length > 0),
    }))
    .filter((q) => q.id.length > 0 && q.texto.length > 0 && q.opcoes.length > 0) as QuizQuestion[];

  const perguntaAtual = perguntas.length > 0 ? perguntas[atual] : null;
  const progresso = perguntas.length > 0 ? ((atual + 1) / perguntas.length) * 100 : 0;
  const trilhasPreview = Object.values(trilhasById).slice(0, 9);
  const trilhasRestantes = Object.values(trilhasById).length - trilhasPreview.length;

  useEffect(() => {
    if (fase !== 'resultado') {
      setRevelarSecundarias(false);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setRevelarSecundarias(true);
    }, 900);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [fase, resultado]);

  function escolher(index: number) {
    if (selecionadaIndex !== null) return;
    setSelecionadaIndex(index);
  }

  function avancar() {
    if (selecionadaIndex === null || !perguntaAtual?.opcoes?.[selecionadaIndex]) return;
    const opcaoSelecionada = perguntaAtual.opcoes[selecionadaIndex];
    const novas = [...respostas, opcaoSelecionada];
    if (atual + 1 < perguntas.length) {
      setTimeout(() => {
        setAtual(atual + 1);
        setSelecionadaIndex(null);
        setRespostas(novas);
      }, 300);
    } else {
      const res = calcularResultado(novas, trilhasById);
      if (!res) {
        reiniciar();
        return;
      }
      setResultado(res);
      setFase('resultado');
    }
  }

  function reiniciar() {
    setFase('intro');
    setAtual(0);
    setRespostas([]);
    setSelecionadaIndex(null);
    setResultado(null);
    setRevelarSecundarias(false);
  }

  return (
    <div className={`${inModal ? 'relative flex h-full flex-col overflow-hidden rounded-[32px] bg-black/70 text-white' : 'min-h-screen bg-black text-white flex flex-col items-center overflow-x-hidden'}`}>
      {/* Animated Background */}
      <div className={`${inModal ? 'absolute inset-0 z-0 overflow-hidden rounded-[32px]' : 'fixed inset-0 z-0'}`}>
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(139,0,139,0.1),transparent_50%)]" />
        <div className="absolute inset-0 hidden md:block opacity-45">
          <div className="ambient-blob ambient-blob-slow absolute top-0 -left-1/4 h-1/2 w-1/2 rounded-full bg-purple-600 mix-blend-multiply" />
          <div className="ambient-blob ambient-blob-slow absolute top-0 -right-1/4 h-1/2 w-1/2 rounded-full bg-pink-600 mix-blend-multiply" style={{ animationDelay: '1.6s' }} />
          <div className="ambient-blob ambient-blob-slow absolute -bottom-1/4 left-1/3 h-1/2 w-1/2 rounded-full bg-fuchsia-600 mix-blend-multiply" style={{ animationDelay: '0.8s' }} />
        </div>
      </div>

      {/* Header */}
      <header className={`${inModal ? 'relative z-20 flex w-full items-center justify-between border-b border-white/10 bg-black/25 px-5 py-5 glass-soft md:px-8' : 'w-full max-w-4xl px-5 md:px-8 pt-8 pb-4 flex items-center justify-between relative z-10'}`}>
        {inModal ? (
          <>
            <div className="w-10" aria-hidden="true" />

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-1 justify-center"
            >
              <img
                src="https://rati.com.br/wp-content/uploads/2020/12/logo-small.png"
                alt="RATI"
                className="h-8 object-contain"
              />
            </motion.div>

            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={onVoltar}
              aria-label="Fechar quiz"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/12 bg-white/5 text-white/75 transition-colors hover:bg-white/10 hover:text-white"
            >
              <X size={18} />
            </motion.button>
          </>
        ) : (
          <>
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={onVoltar}
              className="flex items-center gap-2 text-sm font-semibold text-white/75 hover:text-white transition-colors tracking-wide"
              style={BODY}
            >
              <ArrowLeft size={18} />
              Voltar
            </motion.button>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-center"
            >
              <img
                src="https://rati.com.br/wp-content/uploads/2020/12/logo-small.png"
                alt="RATI"
                className="h-8 object-contain"
              />
            </motion.div>

            {fase === 'quiz' ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="text-sm font-semibold text-white/45 tracking-wide"
                style={BODY}
              >
                {perguntas.length > 0 ? `${atual + 1}/${perguntas.length}` : ''}
              </motion.div>
            ) : (
              <div className="w-20" />
            )}
          </>
        )}
      </header>

      <div
        className={`${inModal ? 'relative z-10 flex-1 overflow-y-auto overflow-x-hidden' : 'w-full'}`}
      >
        {/* INTRO */}
        {fase === 'intro' && (
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl w-full px-5 md:px-8 py-20 flex flex-col items-center gap-10 text-center relative z-10 mx-auto"
          >
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/5 glass-soft border border-white/15">
              <Sparkles size={15} className="text-pink-400" />
              <span className="text-sm font-semibold tracking-wider uppercase" style={BODY}>
                Descoberta Personalizada
              </span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl leading-[1.15] tracking-tight" style={HEADING}>
              {quizTitle}
            </h1>

            <p className="text-lg text-white/82 max-w-2xl leading-[1.8]" style={BODY}>
              {perguntas.length > 0
                ? `${perguntas.length} perguntas rápidas para descobrir por onde começar.`
                : (quizSubtitle || 'O quiz está sendo carregado a partir do WordPress.')}
            </p>

            <div className="flex flex-wrap gap-3 justify-center max-w-3xl">
              {trilhasPreview.map((t) => {
                const Icon = t.icon;
                return (
                  <div
                    key={t.id}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${t.gradiente} bg-opacity-10 border border-white/20 glass-soft`}
                  >
                    <Icon size={13} style={{ color: t.cor }} />
                    <span className="text-xs font-semibold tracking-wide" style={BODY}>
                      {t.nome}
                    </span>
                  </div>
                );
              })}
              {trilhasRestantes > 0 && (
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 glass-soft">
                  <span className="text-xs font-semibold text-white/50 tracking-wide" style={BODY}>
                    +{trilhasRestantes} trilhas
                  </span>
                </div>
              )}
            </div>

            <motion.button
              whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(236, 72, 153, 0.5)' }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setFase('quiz')}
              className="px-12 py-5 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 font-bold text-base tracking-wide shadow-2xl shadow-pink-500/40 mt-4"
              style={BODY}
            >
              Descobrir minha trilha →
            </motion.button>

            <p className="text-sm text-white/45 tracking-wide" style={BODY}>
              100% anônimo · Sem cadastro · 2 minutos
            </p>
          </motion.main>
        )}

        {/* QUIZ */}
        {fase === 'quiz' && (
          perguntas.length === 0 || !perguntaAtual ? (
            <motion.main
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl w-full px-5 md:px-8 py-12 flex flex-col gap-10 relative z-10 mx-auto"
            >
              <div className="p-10 rounded-3xl bg-white/5 glass-soft border border-white/10">
                <h2 className="text-2xl mb-3" style={HEADING}>
                  Quiz indisponível
                </h2>
                <p className="text-white/70 leading-relaxed mb-7" style={BODY}>
                  O quiz ainda não foi carregado pelo WordPress.
                </p>
                <button
                  type="button"
                  onClick={onVoltar}
                  className="px-8 py-3 rounded-full bg-white/10 hover:bg-white/15 transition-colors border border-white/15"
                  style={BODY}
                >
                  Voltar
                </button>
              </div>
            </motion.main>
          ) : (
            <AnimatePresence mode="wait">
              <motion.main
                key={atual}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="max-w-3xl w-full px-5 md:px-8 py-12 flex flex-col gap-10 relative z-10 mx-auto"
              >
              {/* Barra de Progresso */}
              <div className="relative">
                <div className="h-1.5 bg-white/10 rounded-full overflow-hidden glass-soft">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progresso}%` }}
                    className="h-full bg-gradient-to-r from-pink-500 to-purple-600 rounded-full"
                    transition={{ duration: 0.4 }}
                  />
                </div>
                <div className="flex justify-between mt-2.5 text-xs text-white/50 tracking-wide" style={BODY}>
                  <span>Pergunta {atual + 1}</span>
                  <span>{Math.round(progresso)}%</span>
                </div>
              </div>

              {/* Pergunta */}
              <div className="flex flex-col gap-5 p-10 rounded-3xl bg-white/5 glass-soft border border-white/10">
                <div className="inline-flex items-center gap-2 text-pink-400 text-sm font-semibold tracking-wide" style={BODY}>
                  <Sparkles size={15} />
                  Pergunta {atual + 1} de {perguntas.length}
                </div>

                <h2 className="text-3xl md:text-4xl leading-tight" style={HEADING}>
                  {perguntaAtual.texto}
                </h2>

                {perguntaAtual.subtexto && (
                  <p className="text-base text-white/72 leading-relaxed" style={BODY}>
                    {perguntaAtual.subtexto}
                  </p>
                )}
              </div>

              {/* Opções */}
              <div className="flex flex-col gap-4">
                {perguntaAtual.opcoes.map((op, i) => (
                  <motion.button
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.05 }}
                    onClick={() => escolher(i)}
                    disabled={selecionadaIndex !== null}
                    whileHover={selecionadaIndex === null ? { scale: 1.02, x: 4 } : {}}
                    className={`flex items-start gap-5 px-4 py-4 md:px-7 md:py-5 rounded-2xl text-left transition-all ${
                      selecionadaIndex === i
                        ? 'bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-2 border-pink-500/60 shadow-lg shadow-pink-500/20'
                        : 'bg-white/5 glass-soft border-2 border-white/10 hover:border-white/30'
                    } ${selecionadaIndex !== null && selecionadaIndex !== i ? 'opacity-50 cursor-default' : ''}`}
                    style={BODY}
                  >
                    <div className={`w-6 h-6 rounded-full border-2 flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                      selecionadaIndex === i
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 border-transparent'
                        : 'border-white/30'
                    }`}>
                      {selecionadaIndex === i && <Check size={14} className="text-white" />}
                    </div>
                    <span className="text-base leading-[1.7] text-white/88">{op.label}</span>
                  </motion.button>
                ))}
              </div>

              {/* Botão Avançar */}
              <div className="flex justify-end pt-2">
                <motion.button
                  whileHover={selecionadaIndex !== null ? { scale: 1.05 } : {}}
                  whileTap={selecionadaIndex !== null ? { scale: 0.95 } : {}}
                  onClick={avancar}
                  disabled={selecionadaIndex === null}
                  className={`px-9 py-4 rounded-full font-bold text-base tracking-wide transition-all ${
                    selecionadaIndex !== null
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 shadow-lg shadow-pink-500/40'
                      : 'bg-white/5 border-2 border-white/10 text-white/35 cursor-not-allowed'
                  }`}
                  style={BODY}
                >
                  {atual + 1 === perguntas.length ? 'Ver Resultado' : 'Próxima'}
                </motion.button>
              </div>
              </motion.main>
            </AnimatePresence>
          )
        )}

        {/* RESULTADO */}
        {fase === 'resultado' && resultado && (
          <motion.main
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl w-full px-5 md:px-8 py-14 flex flex-col gap-14 relative z-10 mx-auto"
          >
          {/* Label */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-white/5 glass-soft border border-white/15 mb-7">
              <Sparkles size={15} className="text-pink-400" />
              <span className="text-sm font-semibold tracking-wider uppercase" style={BODY}>
                Sua trilha ideal
              </span>
            </div>
            <h2 className="text-2xl text-white/72" style={HEADING}>
              A trilha perfeita para você é
            </h2>
          </div>

          {/* Trilha Principal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative p-8 md:p-10 rounded-3xl overflow-hidden border border-white/20"
          >
            <div className={`absolute inset-0 bg-gradient-to-br ${resultado.principal.gradiente} opacity-15`} />
            <div className="absolute inset-0 glass-strong" />

            <div className="relative z-10 flex flex-col gap-8">
              <div className="relative overflow-hidden rounded-[28px] border border-white/15 bg-white/5 h-[240px] md:h-[320px]">
                <img
                  src={resultado.principal.imagem}
                  alt={resultado.principal.imagemAlt}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${resultado.principal.gradiente} opacity-25`} />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              </div>

              <div className="flex flex-col gap-6">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${resultado.principal.gradiente} text-white font-semibold self-start text-sm tracking-wide`} style={BODY}>
                  <resultado.principal.icon size={18} />
                  Trilha Premium
                </div>

                <h3 className="text-4xl md:text-5xl leading-tight" style={HEADING}>
                  {resultado.principal.nome}
                </h3>

                <p className="text-lg text-white/82 leading-[1.8] max-w-3xl" style={BODY}>
                  {resultado.principal.quizResultText || resultado.principal.descricao}
                </p>

                {resultado.principal.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2.5">
                    {resultado.principal.tags.map((tag: string) => (
                      <span
                        key={tag}
                        className="px-4 py-2 rounded-full bg-white/10 glass-soft border border-white/20 text-sm font-semibold tracking-wide"
                        style={BODY}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {quizCtaTrilhaLink ? (
                  <motion.a
                    href={quizCtaTrilhaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className={`px-9 py-4 rounded-full bg-gradient-to-r ${resultado.principal.gradiente} font-bold text-base tracking-wide shadow-lg flex items-center gap-2.5 self-start text-center`}
                    style={BODY}
                  >
                    <Play size={18} />
                    Começar Trilha
                  </motion.a>
                ) : (
                  <motion.button
                    type="button"
                    disabled
                    className={`px-9 py-4 rounded-full bg-gradient-to-r ${resultado.principal.gradiente} font-bold text-base tracking-wide shadow-lg flex items-center gap-2.5 self-start opacity-45 cursor-not-allowed`}
                    style={BODY}
                  >
                    <Play size={18} />
                    Começar Trilha
                  </motion.button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Secundárias */}
          <div>
            <h4 className="text-2xl mb-8" style={HEADING}>
              Outras trilhas que <span className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent italic">combinam com você</span>
            </h4>

            <div className="grid md:grid-cols-2 gap-6">
              {[resultado.secundaria, resultado.terceira]
                .filter((trilha): trilha is TrilhaInfo => trilha !== null)
                .map((t, i: number) => {
                const Icon = t.icon;
                return (
                  <motion.div
                    key={t.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={revelarSecundarias ? { opacity: 1, y: 0 } : {}}
                    transition={{ delay: i * 0.15 }}
                    whileHover={{ scale: 1.02, y: -4 }}
                    className="flex flex-col gap-6 p-6 rounded-2xl bg-white/5 glass-soft border border-white/10 hover:border-pink-500/50 transition-all cursor-pointer group"
                  >
                    <div className="relative overflow-hidden rounded-[22px] border border-white/10 h-[200px]">
                      <img
                        src={t.imagem}
                        alt={t.imagemAlt}
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className={`absolute inset-0 bg-gradient-to-t ${t.gradiente} opacity-20`} />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    </div>

                    <div className="flex items-start gap-2.5">
                      <div
                        className={`w-12 h-12 rounded-xl bg-gradient-to-r ${t.gradiente} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}
                      >
                        <Icon size={22} className="text-white" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <h5 className="text-xl mb-3" style={HEADING}>
                          {t.nome}
                        </h5>

                        <p className="text-white/78 leading-[1.8]" style={BODY}>
                          {t.quizResultText || t.descricao}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-7 items-center text-center p-12 rounded-3xl bg-white/5 glass-soft border border-white/10">
            <p className="text-lg text-white/80 max-w-2xl leading-[1.8]" style={BODY}>
              A RATI tem conteúdo especializado para cada uma dessas trilhas. Acesso ilimitado, sem julgamento, com prazer.
            </p>

            <div className="flex flex-wrap gap-4 justify-center">
              {quizCtaTrilhaLink ? (
                <motion.a
                  href={quizCtaTrilhaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05, boxShadow: '0 0 40px rgba(236, 72, 153, 0.5)' }}
                  whileTap={{ scale: 0.95 }}
                  className="px-10 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 font-bold text-base tracking-wide shadow-lg shadow-pink-500/40 text-center"
                  style={BODY}
                >
                  Quero começar — 7 dias de garantia
                </motion.a>
              ) : (
                <motion.button
                  type="button"
                  disabled
                  className="px-10 py-4 rounded-full bg-gradient-to-r from-pink-500 to-purple-600 font-bold text-base tracking-wide shadow-lg shadow-pink-500/40 opacity-45 cursor-not-allowed"
                  style={BODY}
                >
                  Quero começar — 7 dias de garantia
                </motion.button>
              )}

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={reiniciar}
                className="px-10 py-4 rounded-full border border-white/25 font-semibold text-base tracking-wide glass-soft bg-white/5 hover:bg-white/10 transition-colors"
                style={BODY}
              >
                Refazer o quiz
              </motion.button>
            </div>
          </div>
          </motion.main>
        )}
      </div>
    </div>
  );
}
